import type { Member } from '@hive/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { queryKeys } from './keys';

/**
 * The members row for a signed-in auth user, or null when they aren't a studio
 * member (not on the allowlist). RLS hides every row from non-members.
 */
export function useMemberForUser(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.memberForUser(userId ?? ''),
    enabled: !!userId,
    queryFn: async (): Promise<Member | null> => {
      const { data, error } = await supabase().from('members').select('*').eq('user_id', userId!).maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

/** Everyone in the studio, Claude included, by name. */
export function useMembers() {
  return useQuery({
    queryKey: queryKeys.members,
    queryFn: async (): Promise<Member[]> => {
      const { data, error } = await supabase().from('members').select('*').order('display_name');
      if (error) throw error;
      return data;
    },
  });
}

/** Changes the signed-in member's own name and nickname (RLS allows only their own row). */
export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: { display_name?: string; nickname?: string | null } }) => {
      const { data, error } = await supabase().from('members').update(patch).eq('id', id).select('id');
      if (error) {
        if (error.code === '23505') throw new Error(`Someone already goes by @${patch.nickname}.`);
        if (error.code === '23514') throw new Error('That name or nickname isn’t allowed.');
        throw error;
      }
      if (!data.length) throw new Error('You can only change your own profile.');
    },
    // Both the members list and the signed-in member live under 'members'.
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.members }),
  });
}
